if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          form: "form",
          inputId: "[name=id]",
          submitButton: '[name="add"]',
          errorWrapper: ".f-product-form__error-message-wrapper",
          customFields: ["[data-product-custom-field]"],
          customCartButton: "[data-custom-atc]",
          customQuickView: "[data-custom-atc-qv]"
        };

        this.domNodes = queryDomNodes(this.selectors, this);

        this.form = this.domNodes.form;
        this.submitButton = this.domNodes.submitButton;
        this.domNodes.inputId.disabled = false;
        this.Notification = window.FoxTheme.Notification;
        this.notificationType = this.dataset.notificationType;
        this.customFields = document.querySelectorAll(
          this.selectors.customFields
        );
        this.cartDrawer = document.querySelector("cart-drawer");
        this.mainCart = document.querySelector("cart-items");
        this.type = this.dataset.type;

        this.variantIdInput = this.domNodes.inputId;
        this.errorWrapperHideTimeoutId = null;

        this.errorWrapper =
          this.type === "product-card"
            ? this.closest(".product-card")
            : this.domNodes.errorWrapper;

        // Store bound handlers for cleanup
        this.boundSubmitHandler = this.onSubmitHandler.bind(this);
        this.boundStringingChangeHandler = this.handleStringingChange.bind(this);
        
        // Flag to prevent double submission
        this.isSubmitting = false;
        
        // Store listeners for cleanup
        this.stringingSelectListeners = new Map();
        this.customButtonHandlers = new Map();

        // Check for custom button - if it exists, use click handler only
        // If no custom button, use form submit handler
        this.customCartButton = this.querySelector(
          this.selectors.customCartButton
        );
        
        if (this.customCartButton) {
          // Custom button exists - handle via click ONLY
          // Do NOT add form submit listener to prevent double-firing
          const clickHandler = (e) => {
            // Prevent all default behaviors and event propagation
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            // Call our handler
            this.boundSubmitHandler(e);
          };
          
          this.customCartButton.addEventListener("click", clickHandler, true);
          this.customButtonHandlers.set(this.customCartButton, clickHandler);
         
          if (this.form) {
            const formSubmitBlocker = (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              return false;
            };
            // Use capture phase with high priority to intercept early
            this.form.addEventListener("submit", formSubmitBlocker, true);
            this.customButtonHandlers.set(this.form, formSubmitBlocker);
          }
        } else {
          // No custom button - use form submit listener normally
          if (this.form) {
            this.form.addEventListener("submit", this.boundSubmitHandler);
          }
        }

        this.customQuickView = this.querySelector(
          this.selectors.customQuickView
        );
        if (this.customQuickView) {
          const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Prevent form submission if button is inside a form
            if (this.form && e.target.closest('form') === this.form) {
              e.stopImmediatePropagation();
            }
            this.boundSubmitHandler(e);
          };
          this.customQuickView.addEventListener("click", clickHandler);
          this.customButtonHandlers.set(this.customQuickView, clickHandler);
        }
      
        // Add event listeners to hide/show validation message when stringing service fields change
        const stringingSelects = this.querySelectorAll('#stringing-options, #string-tightness, #logo-color');
        if (stringingSelects && stringingSelects.length > 0) {
          stringingSelects.forEach(select => {
            select.addEventListener('change', this.boundStringingChangeHandler);
            this.stringingSelectListeners.set(select, this.boundStringingChangeHandler);
          });
        }
      }

      handleStringingChange() {
        // Check both product-info (regular) and qv-product-info (quickview) contexts
        const productwrapper = this.closest('product-info') || this.closest('.qv-product-info')?.closest('.product-title-info');
        const validationMessageEl = productwrapper?.querySelector('#stringing-validation-message');
        if (validationMessageEl) {
          // Check if all fields are filled (using same logic as form submission)
          let filledCount = 0;
          let totalFields = 0;
          
          if (productwrapper.querySelector('.dropdown-container')) {
            productwrapper.querySelectorAll('.dropdown-container select').forEach((each) => {
              totalFields += 1;
              if (each.value && each.value !== '') {
                filledCount += 1;
              }
            });
          }

          if (filledCount === totalFields && totalFields > 0) {
            // All fields are filled - hide message
            validationMessageEl.style.display = 'none';
          } else if (filledCount > 0 && filledCount < totalFields) {
            // Some fields filled but not all - show message
            const validationMessage = validationMessageEl.getAttribute('data-validation-message') || 'Please fill all the required fields to add a string service';
            const iconTemplate = document.querySelector('#validation-warning-icon-template');
            const iconHTML = iconTemplate ? iconTemplate.innerHTML : '';
            validationMessageEl.innerHTML = `${iconHTML}<span>${validationMessage}</span>`;
            validationMessageEl.style.display = 'flex';
          } else {
            // No fields filled - hide message
            validationMessageEl.style.display = 'none';
          }
        }
      }

      disconnectedCallback() {
        // Clean up all event listeners to prevent duplicates
        if (this.form && this.boundSubmitHandler) {
          this.form.removeEventListener("submit", this.boundSubmitHandler);
        }
        
        // Remove custom button listeners
        this.customButtonHandlers.forEach((handler, element) => {
          element.removeEventListener("click", handler);
        });
        this.customButtonHandlers.clear();
        
        // Remove stringing select listeners
        this.stringingSelectListeners.forEach((handler, select) => {
          select.removeEventListener('change', handler);
        });
        this.stringingSelectListeners.clear();
        
        // Reset submission flag
        this.isSubmitting = false;
      }

      validateForm(form) {
        const missingFields = [];
        if (!form) return missingFields;
        const fieldSelectors =
          '[data-product-custom-field] [name][required]:not([hidden]):not([type="hidden"])';
        const requiredFields = form.querySelectorAll(fieldSelectors);
        requiredFields.forEach((field) => {
          field.classList.remove("form-control--warning");
          if (field.type === "radio") {
            const buttons = form.querySelectorAll(
              `input[name="${field.name}"]`
            );
            const selected = Array.from(buttons).some((btn) => btn.checked);
            if (!selected) {
              missingFields.push(field);
              field.classList.add("form-control--warning");
            }
          } else if (!field.value) {
            missingFields.push(field);
            field.classList.add("form-control--warning");
          }
        });
        return missingFields;
      }

      scheduleErrorWrapperHide(wrapper, delay = 3600) {
        if (!wrapper) return;
        clearTimeout(this.errorWrapperHideTimeoutId);
        this.errorWrapperHideTimeoutId = setTimeout(() => {
          wrapper.style.display = "none";
        }, delay);
      }

      onSubmitHandler(evt) {
        // CRITICAL: Prevent double submission - check FIRST, before anything else
        // Use synchronous check to prevent race conditions
        if (this.isSubmitting === true) {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          return false;
        }

        // Check loading state early
        if (this.submitButton && this.submitButton.classList.contains("btn--loading")) {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          return false;
        }

        // Set submission flag IMMEDIATELY and SYNCHRONOUSLY
        // This must happen before any async operations
        this.isSubmitting = true;
        
        // Also set a timestamp to detect stale submissions
        this.submissionTimestamp = Date.now();

        let productwrapper = evt.target.closest('product-info');
        if (
          productwrapper?.classList.contains('custom-available') && productwrapper.querySelector('#stringing-options')?.selectedIndex
        ) {
          let mainProductInCartQuantity = 0;
          let stringCartQuantity = 0;
          let onlyMainProduct = false;
          let onlyStringProduct = false;
          
          // Get variant picker element with null check
          let variantPickerInput = productwrapper.querySelector('.variant-picker__field.size-field .variant-picker__option input[checked]');
          let mainProductTotalAvailableQuantity = 0;
          let mainProductVariantId = null;
          
          if (variantPickerInput) {
            mainProductTotalAvailableQuantity = Number(variantPickerInput.getAttribute('data-variantavailable') || 0);
            mainProductVariantId = variantPickerInput.getAttribute('data-variantid');
          } else {
            console.warn('Variant picker input not found, skipping inventory check');
          }
          
          let stringTotalAvailableQuantity;
          let stringVariantId;

          if(productwrapper.querySelector('#stringing-options')?.selectedIndex){
            let select = productwrapper.querySelector("#stringing-options");
            let selectedOption = select.options[select.selectedIndex];
            if (selectedOption) {
              stringTotalAvailableQuantity = Number(selectedOption.getAttribute("data-available") || 0);
              stringVariantId = selectedOption.value;
            }
          }

          if(mainProductVariantId && document.querySelector('#CartDrawer')){
            document.querySelectorAll('.f-cart-drawer__item').forEach((each)=>{
              if(each.getAttribute('data-cart-item-variant-id') == mainProductVariantId){
                mainProductInCartQuantity += Number(each.querySelector('.f-quantity__wrapper .f-quantity__input')?.value || 0)
              }
              if(stringVariantId && each.getAttribute('data-cart-item-variant-id') == stringVariantId){
                stringCartQuantity += Number(each.querySelector('.f-quantity__wrapper .f-quantity__input')?.value || 0)
              }
            })
          }else{
            mainProductInCartQuantity = 0;
            stringCartQuantity = 0;
          }
          // console.log('129', mainProductTotalAvailableQuantity, stringTotalAvailableQuantity, mainProductInCartQuantity, stringCartQuantity);

          if(mainProductTotalAvailableQuantity > 0 && mainProductTotalAvailableQuantity <= mainProductInCartQuantity){
            onlyMainProduct = true;
          }
          if (productwrapper.querySelector('#stringing-options')?.selectedIndex){
            if(stringTotalAvailableQuantity && stringTotalAvailableQuantity <= stringCartQuantity){
              onlyStringProduct = true;
            }
          }

          // console.log('229', onlyMainProduct, onlyStringProduct)
          if(onlyMainProduct || onlyStringProduct){
            const errorWrapper = productwrapper.querySelector('.f-product-form__error-message-wrapper');
            if (errorWrapper) {
              errorWrapper.style.display = "block"; 
            }
            
            this.Notification.show({
              target: errorWrapper,
              method: "appendChild",
              type: "warning",
              message: "Cannot add more items to the cart. Maximum quantity reached.",
              last: 3000,
              sticky: this.notificationType === "toast",
            });
            this.scheduleErrorWrapperHide(errorWrapper);
            this.isSubmitting = false;
            return;
          }
        }
        
        evt.preventDefault();
        evt.stopPropagation();
        const missing = this.validateForm(
          this.form.closest(".f-product-single__blocks")
        );

        if (missing && missing.length > 0) {
          this.isSubmitting = false;
          if (this.domNodes.errorWrapper) this.domNodes.errorWrapper.style.display = "block";
          this.scheduleErrorWrapperHide(this.domNodes.errorWrapper);
          return this.Notification.show({
            target: this.domNodes.errorWrapper,
            method: "appendChild",
            type: "warning",
            message: window.FoxThemeStrings.requiredField,
          });
        }

        if(this.submitButton){
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButton.classList.add("btn--loading");
        }
        const config = {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };

      // const formData = new FormData(this.form);

      //Check if customisation available with product****************************
        // Check if customisation is available
        if (
          (evt.target.closest('.product-title-info')?.classList.contains('custom-available')) || (evt.target.closest('.qv-product-info')?.classList.contains('custom-available') )
        ) {
          let counter = 0;
          let numberOfSelectOption = 0;
          
          if (productwrapper.querySelector('.dropdown-container')) {
            productwrapper.querySelectorAll('.dropdown-container select').forEach((each, index) => {
              // Check if select has a value (not empty and not the placeholder)
              // Also check if it's the logo-color select - use text content for validation
              let hasValue = false;
              if (each.id === 'logo-color') {
                // For logo-color, check if a real option is selected (not placeholder)
                if (each.selectedIndex > 0 && each.options[each.selectedIndex]) {
                  let selectedOption = each.options[each.selectedIndex];
                  hasValue = selectedOption.textContent.trim() !== '' && 
                            selectedOption.value !== '' &&
                            !selectedOption.textContent.includes('Select'); // Exclude placeholder text
                }
              } else {
                // For other selects, check value
                hasValue = each.value && each.value.trim() !== '';
              }
              
              if (hasValue) {
                counter += 1;
              }
              numberOfSelectOption = index + 1;
            });
          }
          // Main product
          let main_prod_id = productwrapper.querySelector('product-form input[name="id"]').value;
          let main_prod_name = productwrapper.querySelector('.f-product-single__title.h2').innerHTML;
          

          if (counter === numberOfSelectOption) {
            // Hide validation message when all fields are filled
            const validationMessageEl = productwrapper.querySelector('#stringing-validation-message');
            if (validationMessageEl) {
              validationMessageEl.style.display = 'none';
            }
            
            let formDataCustom = new FormData();
            let stringOptionElement = productwrapper.querySelector("#stringing-options");
            let stringOption = stringOptionElement?.value;
            
            let logoColor =  productwrapper.querySelector("#logo-color")?.value;
            let logoColorFormatted = logoColor ? logoColor.trim().split(' ').join('') : '';
            let stringTightness =  productwrapper.querySelector("#string-tightness")?.value;
            let freestring_lineitem = stringOptionElement?.getAttribute('data-line-item');
            
            let selectedOption = stringOptionElement?.options[stringOptionElement.selectedIndex];
            let stringPrice =  selectedOption ? selectedOption.getAttribute("data-price") : '';
            let stringType = selectedOption ? selectedOption.getAttribute("data-string-type") : "paid";

            let serviceChargeProductVarId = Number(productwrapper.querySelector('#service-charge-details')?.getAttribute('data-varid'));
            let unique_key = `${main_prod_id}-${selectedOption?.value}-${serviceChargeProductVarId}-${stringTightness}-${logoColorFormatted}`;

            // Get translated property names from JSON script tag (translatable via Translate and Adapt)
            let propertyNames = {
              logoColor: 'Logo Color',
              stringTension: 'String Tension'
            };
            try {
              const propertiesScript = document.querySelector('#stringing-service-properties');
              if (propertiesScript) {
                const parsed = JSON.parse(propertiesScript.textContent);
                propertyNames.logoColor = parsed.logoColorProperty || propertyNames.logoColor;
                propertyNames.stringTension = parsed.stringTensionProperty || propertyNames.stringTension;
              }
            } catch (e) {
              console.warn('Failed to parse stringing service properties:', e);
            }

            // console.log('Customization details:', main_prod_id, unique_key);

            // Stringing service product (if applicable)
            let stringServiceCharge = productwrapper.querySelector('#service-charge-details')?.getAttribute('data-service-product');
            let serviceCharge = productwrapper.querySelector('#service-charge-details').getAttribute('data-stringing-charge');
            
            // Track item index to ensure sequential indices
            let itemIndex = 0;
            
            if (stringServiceCharge !== 'none') {
              formDataCustom.append(`items[${itemIndex}][id]`, stringServiceCharge);
              formDataCustom.append(`items[${itemIndex}][quantity]`, 1);
              formDataCustom.append(`items[${itemIndex}][properties][_unique_id]`, unique_key);
              formDataCustom.append(`items[${itemIndex}][properties][_product_type]`, 'CustomAddon');
              formDataCustom.append(`items[${itemIndex}][properties][_custom_product_name]`, 'StringService');
              formDataCustom.append(`items[${itemIndex}][properties][_Product]`, main_prod_name);
              if (logoColor) formDataCustom.append(`items[${itemIndex}][properties][${propertyNames.logoColor}]`, logoColor);
              if (stringTightness) formDataCustom.append(`items[${itemIndex}][properties][${propertyNames.stringTension}]`, stringTightness);
              itemIndex++;
            } 

            // Additional string product (if selected)
            if (stringOption) {
              formDataCustom.append(`items[${itemIndex}][id]`, stringOption);
              formDataCustom.append(`items[${itemIndex}][quantity]`, 1);
              formDataCustom.append(`items[${itemIndex}][properties][_unique_id]`, unique_key);
              formDataCustom.append(`items[${itemIndex}][properties][_product_type]`, 'CustomAddon');
              formDataCustom.append(`items[${itemIndex}][properties][_custom_product_name]`, 'StringSelected');
              formDataCustom.append(`items[${itemIndex}][properties][_Product]`, main_prod_name);
              if (stringType === "free") {
                if (freestring_lineitem !== "") {
                  formDataCustom.append(`items[${itemIndex}][properties][_free_string]`, freestring_lineitem);
                } else {
                  formDataCustom.append(`items[${itemIndex}][properties][String Price]`, stringType);
                }
              }
              itemIndex++;
            }

            // Main product (always added last)
            formDataCustom.append(`items[${itemIndex}][id]`, main_prod_id);
            formDataCustom.append(`items[${itemIndex}][quantity]`, 1);
            formDataCustom.append(`items[${itemIndex}][properties][_unique_id]`, unique_key);
            formDataCustom.append(`items[${itemIndex}][properties][_product_type]`, "MainProduct");
            formDataCustom.append(`items[${itemIndex}][properties][_string_product_price]`, stringPrice);
            formDataCustom.append(`items[${itemIndex}][properties][_stringing_service_charge]`, serviceCharge);

            config.body = formDataCustom;
          }
          else{
            // Check if any stringing service fields are selected but not all
            if (counter > 0 && counter < numberOfSelectOption) {
              // Show validation message in the product customisation block
              const validationMessageEl = productwrapper.querySelector('#stringing-validation-message');
              if (validationMessageEl) {
                // Get validation message from data attribute or use default
                const validationMessage = validationMessageEl.getAttribute('data-validation-message') || 'Please fill all the required fields to add a string service';
                
                validationMessageEl.style.display = 'flex';
                
                // Get the icon template and clone it
                const iconTemplate = document.querySelector('#validation-warning-icon-template');
                const iconHTML = iconTemplate ? iconTemplate.innerHTML : '';
                
                validationMessageEl.innerHTML = `${iconHTML}<span>${validationMessage}</span>`;
                
                // Scroll to the validation message
                validationMessageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                
                // Remove loading state
                this.isSubmitting = false;
                if(this.submitButton){
                  this.submitButton.classList.remove("btn--loading");
                  this.submitButton.removeAttribute("aria-disabled");
                }
              }
              return; // Prevent form submission
            }
            
            // Hide validation message if all fields are empty
            const validationMessageEl = productwrapper.querySelector('#stringing-validation-message');
            if (validationMessageEl) {
              validationMessageEl.style.display = 'none';
            }
            
            let formDataCustom = new FormData();
            formDataCustom.append("items[0][id]", main_prod_id);
            formDataCustom.append("items[0][quantity]", 1);
            config.body = formDataCustom;
          }
         
        } 
        else {
          // Default add to cart flow
          let formDataDefault = new FormData(this.form);
          config.body = formDataDefault;
        }

      //End of customisation code************************************************


        if (this.cartDrawer) {
          config.body.append(
            "sections",
            this.cartDrawer.getSectionsToRender().map((section) => section.id)
          );
        }

        if (this.mainCart) {
          config.body.append(
            "sections",
            this.mainCart
              .getSectionsToRender()
              .map((section) => section.section)
          );
        }

        if (this.mainCart || this.cartDrawer) {
          config.body.append("sections_url", window.location.pathname);
        }

        async function updateDrawer() {
          let response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
          let result = await response.text();
          const html = new DOMParser().parseFromString(result, 'text/html');
          
          const newCartDrawer = html.querySelector('#Drawer-Cart');
          newCartDrawer ? document.querySelector("#Drawer-Cart").innerHTML = newCartDrawer.innerHTML : '';
          document.querySelector('drawer-opener[data-drawer="#Drawer-Cart"] a').click();

        }

        // config.body = formData; 
        const { FoxThemeSettings, FoxThemeStrings } = window;
        fetch(`${FoxThemeSettings.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if(response.status == '422'){
                if(!response.message.includes('The maximum quantity of this item is already in your cart'))
                updateDrawer();
                FoxThemeCartHelpers.getState().then((state) => {
                  FoxThemeCartHelpers.updateCartCount(state.item_count);
                });
              }

              window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: config.body.get("id"),
                errors: response.description,
                message: response.message,
              });
              this.error = true;
              if (this.errorWrapper) this.errorWrapper.style.display = "block";
              this.scheduleErrorWrapperHide(this.errorWrapper);
              return this.Notification.show({
                target:
                  this.notificationType === "toast"
                    ? document.body
                    : this.errorWrapper,
                method: "appendChild",
                type: "warning",
                message: response.description || response.message,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }

            if (
              !FoxThemeSettings.cart.openDrawerWhenAdded ||
              FoxThemeSettings.cart.cartType === "page"
            ) {
              this.Notification.show({
                target: this.errorWrapper,
                method: "appendChild",
                type: "success",
                message: FoxThemeStrings.item_added,
                last: 3000,
                sticky: this.notificationType === "toast",
              });
            }
            if (FoxThemeSettings.cart.cartType === "page") {
              FoxThemeCartHelpers.getState().then((state) => {
                FoxThemeCartHelpers.updateCartCount(state.item_count);
              });
            }

            this.error = false;
            window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            // Reset submission flag and loading state
            this.isSubmitting = false;
            if(this.submitButton){
              this.submitButton.classList.remove("btn--loading");
              this.submitButton.removeAttribute("aria-disabled");
            }
          });
      }
    }
  );
}
